/**
 * Investigation state: what the player has heard, learned, and said.
 *
 * This owns the mutable half of the story. `story.ts` computes what a state *means*; this decides
 * when a state changes and makes sure the change reaches Postgres.
 *
 * Everything here goes through the Chapter 1 sync layer, so the rules established there still
 * hold: the server is the authority, IndexedDB is a cache, writes are debounced, and a stale
 * write loses to whatever another device did (409 → adopt the server's copy). A clue is a fact
 * about an account, not about a browser tab.
 */
import type { ProgressData, ProgressSync } from '../state/progress.js';
import { Store } from '../state/store.js';
import type { ActiveSignal, ScheduledSignal } from '../world/matrix.js';
import type { WorldTime } from '../world/clock.js';
import { formatGameClock } from '../world/clock.js';
import type { PackGate } from '../content/schema.js';
import { applyEffects, effectsWouldChange, gatePasses, stateOf, type StoryState } from './gates.js';
import { advanceToPlayableNode, type ResolvedNode, type Story } from './story.js';

/** How long a signal must be held before its effects fire, when the pack does not say. */
export const DEFAULT_LISTEN_SECONDS = 4;

export interface DialoguePosition {
  /** Node the conversation is sitting on. */
  nodeId: string;
  /** True once the conversation reached a terminal node. */
  done: boolean;
}

/** The story half of a save. Lives inside `ProgressData.investigation`. */
export interface InvestigationData {
  clues: string[];
  events: string[];
  dialogue: Record<string, DialoguePosition>;
  /** Id of the ending reached, once one has been. */
  endingId: string | null;
  endingAt: string | null;
}

export function emptyInvestigation(): InvestigationData {
  return { clues: [], events: [], dialogue: {}, endingId: null, endingAt: null };
}

export interface LogEntry {
  khz: number;
  signalId?: string;
  label?: string;
  /** Real-world timestamp, ISO. */
  at: string;
  /** In-game time it was heard, for the log's own sense of when. */
  gameDay?: number;
  gameTime?: string;
  note?: string;
}

/** What the UI subscribes to. Recomputed whenever the underlying save changes. */
export interface InvestigationView {
  state: StoryState;
  clues: ReturnType<Story['heldClues']>;
  progress: ReturnType<Story['progress']>;
  ending: ReturnType<Story['endingFor']>;
  log: LogEntry[];
  /** The conversation currently open, if the player is in one. */
  dialogue: {
    signalId: string;
    dialogueId: string;
    /** Who is on the other end, for the panel to name. */
    label: string;
    node: ResolvedNode;
  } | null;
  /**
   * Set when a conversation ended because the other party stopped being received.
   *
   * Chapter 6 made this possible: an NPC can walk out of a conversation by moving to another
   * station. Before roaming, a signal only stopped if the player turned the dial, so a dialogue
   * that floated free of the radio was harmless. Now it would leave someone talking to nobody.
   */
  interrupted: { label: string; reason: 'moved' | 'tuned-away' } | null;
}

/**
 * Tracks how long the player has been holding one signal.
 *
 * Listening is the verb of this game, so it has to *cost* something: a clue that could be picked
 * up by sweeping past at speed would make the band a list of collectables rather than something
 * you have to sit with. The dwell timer is what turns tuning into listening.
 */
interface Dwell {
  signalId: string;
  /** Real ms at which the player locked on. */
  since: number;
  /** True once effects have been granted, so they fire once per lock-on, not per tick. */
  granted: boolean;
}

export class Investigation {
  readonly view: Store<InvestigationView>;

  private readonly story: Story;
  private readonly sync: ProgressSync;
  private dwell: Dwell | null = null;
  private openDialogueId: string | null = null;
  private openSignalId: string | null = null;
  private openSignalKhz: number | null = null;
  private openSignalLabel: string | null = null;
  private interrupted: InvestigationView['interrupted'] = null;
  private unsubscribeProgress: () => void;

  constructor(story: Story, sync: ProgressSync) {
    this.story = story;
    this.sync = sync;
    this.view = new Store<InvestigationView>(this.computeView());
    // The save is the source of truth, so the view follows it — including when the server wins a
    // conflict and hands back a state this tab never produced.
    this.unsubscribeProgress = sync.progress.subscribe(() => this.refresh());
  }

  dispose(): void {
    this.unsubscribeProgress();
  }

  /** The story's opening framing, shown on the truth surface. */
  premise(): string | undefined {
    return this.story.premise;
  }

  /** The player's story state, read from the save. */
  storyState(): StoryState {
    const data = this.sync.progress.get().investigation ?? emptyInvestigation();
    return stateOf(data.clues, data.events);
  }

  /** Is this signal available to this player? Asked by the matrix before precedence is applied. */
  isSignalGateOpen(signal: ScheduledSignal): boolean {
    return this.isGateOpen(signal.requires);
  }

  /** Whether a gate is open right now. The signal-shaped wrapper above is the common case. */
  isGateOpen(requires: PackGate | undefined): boolean {
    return gatePasses(requires, this.storyState());
  }

  /** The same question, for a signal already resolved. */
  isSignalUnlocked(signal: ActiveSignal): boolean {
    return this.isSignalGateOpen(signal.signal);
  }

  /**
   * Called continuously while a signal is tuned in, and with `null` when it is not.
   *
   * Returns the clue ids granted by this call, so the UI can announce them — a clue arriving
   * unannounced would be indistinguishable from nothing happening.
   */
  onTuned(signal: ActiveSignal | null, time: WorldTime, nowMs = Date.now()): string[] {
    if (!signal) {
      this.dwell = null;
      return [];
    }

    const id = signal.signal.id;
    if (this.dwell?.signalId !== id) {
      this.dwell = { signalId: id, since: nowMs, granted: false };
      return [];
    }
    if (this.dwell.granted) return [];

    const required = (signal.signal.listenSeconds ?? DEFAULT_LISTEN_SECONDS) * 1000;
    if (nowMs - this.dwell.since < required) return [];

    this.dwell.granted = true;
    this.logHeard(signal, time);

    const before = this.storyState();
    const effects = signal.signal.effects;
    if (!effectsWouldChange(before, effects)) return [];

    const after = applyEffects(before, effects);
    this.commit(after);
    return (effects?.clues ?? []).filter((clue) => !before.clues.has(clue));
  }

  /** Add a frequency-log entry for a signal the player actually heard. Idempotent per signal. */
  private logHeard(signal: ActiveSignal, time: WorldTime): void {
    const entry: LogEntry = {
      khz: Math.round(signal.frequencyKhz),
      signalId: signal.signal.id,
      ...(signal.signal.label ? { label: signal.signal.label } : {}),
      at: new Date().toISOString(),
      gameDay: time.game.day,
      gameTime: formatGameClock(time.game),
    };

    this.sync.patch((current) => {
      // One entry per signal: a drifting station heard on four nights is one thing found, not
      // four. Its frequency is updated to wherever it was heard most recently, which is what a
      // player would have written down.
      const existing = current.frequencyLog.findIndex((item) => item.signalId === entry.signalId);
      const log = [...current.frequencyLog];
      if (existing >= 0) log[existing] = { ...log[existing]!, ...entry };
      else log.push(entry);
      return { ...current, frequencyLog: log };
    });
  }

  /** A note the player typed against a logged frequency. */
  setNote(signalId: string, note: string): void {
    this.sync.patch((current) => ({
      ...current,
      frequencyLog: current.frequencyLog.map((entry) =>
        entry.signalId === signalId ? { ...entry, note } : entry,
      ),
    }));
  }

  /**
   * Whether the dialogue on this signal starts by NPC broadcast ('npc')
   * or requires the user to hail/transmit first ('user').
   */
  dialogueInitiation(signal: ActiveSignal): 'npc' | 'user' {
    const dialogueId = signal.signal.dialogue;
    if (!dialogueId) return 'npc';
    const dialogue = this.story.dialogue(dialogueId);
    return dialogue?.initiation ?? 'npc';
  }

  /**
   * Would `openDialogue` succeed? Asked by the UI every poll to decide whether to show the
   * "answer" affordance, so it must be free of side effects — hence a separate query rather than
   * calling openDialogue and undoing it.
   */
  canOpenDialogue(signal: ActiveSignal): boolean {
    const dialogueId = signal.signal.dialogue;
    if (!dialogueId) return false;
    if (!this.story.dialogue(dialogueId)) return false;
    if (this.dialogueData()[dialogueId]?.done) return false;
    // Already in this conversation: the panel is open, so there is nothing to offer.
    return this.openDialogueId !== dialogueId;
  }

  /**
   * Open the conversation a signal carries, resuming where it was left.
   *
   * Returns false when the signal has no dialogue or the conversation is already finished — the
   * caller uses that to decide whether to offer a "transmit" affordance at all.
   */
  openDialogue(signal: ActiveSignal): boolean {
    const dialogueId = signal.signal.dialogue;
    if (!dialogueId) return false;

    const dialogue = this.story.dialogue(dialogueId);
    if (!dialogue) return false;

    const saved = this.dialogueData()[dialogueId];
    if (saved?.done) return false;

    const startNode = saved?.nodeId ?? dialogue.entry;
    const arrival = advanceToPlayableNode(this.story, dialogueId, startNode, this.storyState());
    if (!arrival.node) return false;

    this.openDialogueId = dialogueId;
    this.openSignalKhz = Math.round(signal.frequencyKhz);
    this.openSignalLabel = signal.signal.label ?? signal.signal.id;
    this.interrupted = null;
    this.openSignalId = signal.signal.id;
    this.commit(arrival.state, (data) => ({
      ...data,
      dialogue: {
        ...data.dialogue,
        [dialogueId]: { nodeId: arrival.node!.id, done: arrival.node!.terminal },
      },
    }));
    return true;
  }

  /**
   * Break off an open conversation when the other party is no longer being received.
   *
   * Called from the same poll as `onTuned`, but kept separate: `onTuned` is about dwell time and
   * clues, and this is about whether there is still anyone on the other end. Splitting them means
   * this rule can be tested on its own, which matters because it is the one piece of Chapter 6
   * that can strand a player.
   *
   * The player keeps their place in the tree either way — tuning back resumes from the same node,
   * because the conversation's position lives in the save rather than in this object. So breaking
   * off costs nothing and never has to be undone.
   *
   * Returns true when a conversation was actually broken off.
   */
  breakOffIfGone(signal: ActiveSignal | null, tunedKhz: number): boolean {
    if (!this.openDialogueId || !this.openSignalId) return false;
    if (signal && signal.signal.id === this.openSignalId) return false;

    // Who left: the dial or the NPC. If the player is still parked where the conversation
    // started, they did not walk away — the other party did.
    const stillParked =
      this.openSignalKhz !== null && Math.abs(Math.round(tunedKhz) - this.openSignalKhz) <= 1;

    this.interrupted = {
      label: this.openSignalLabel ?? this.openSignalId,
      reason: stillParked ? 'moved' : 'tuned-away',
    };
    this.openDialogueId = null;
    this.openSignalId = null;
    this.openSignalKhz = null;
    this.openSignalLabel = null;
    this.refresh();
    return true;
  }

  /** Dismiss the interruption notice once the player has been told. */
  acknowledgeInterruption(): void {
    if (!this.interrupted) return;
    this.interrupted = null;
    this.refresh();
  }

  closeDialogue(): void {
    this.interrupted = null;
    this.openSignalKhz = null;
    this.openSignalLabel = null;
    this.openDialogueId = null;
    this.openSignalId = null;
    this.refresh();
  }

  /**
   * Take an option. Applies its effects, moves to the next node, and stores the position.
   *
   * Returns the clue ids gained, for the same reason `onTuned` does.
   */
  chooseOption(optionId: string): string[] {
    const dialogueId = this.openDialogueId;
    if (!dialogueId) return [];

    const position = this.dialogueData()[dialogueId];
    if (!position) return [];

    const current = this.story.resolveNode(dialogueId, position.nodeId, this.storyState());
    const chosen = current?.options.find((option) => option.id === optionId);
    if (!chosen) return [];

    const before = this.storyState();
    let after = applyEffects(before, chosen.option.effects);

    let nodeId = position.nodeId;
    let done = true;
    if (chosen.next) {
      const arrival = advanceToPlayableNode(this.story, dialogueId, chosen.next, after);
      if (arrival.node) {
        after = arrival.state;
        nodeId = arrival.node.id;
        done = arrival.node.terminal;
      }
    }

    this.commit(after, (data) => ({
      ...data,
      dialogue: { ...data.dialogue, [dialogueId]: { nodeId, done } },
    }));

    // Clearing the open conversation has to happen *before* the view is recomputed, or the panel
    // keeps rendering a conversation the save says is finished — and the player is left looking
    // at options that no longer do anything. `commit` refreshes, so this needs its own refresh
    // after it.
    if (done) {
      this.openDialogueId = null;
      this.openSignalId = null;
      this.refresh();
    }
    return (chosen.option.effects?.clues ?? []).filter((clue) => !before.clues.has(clue));
  }

  private dialogueData(): Record<string, DialoguePosition> {
    return this.sync.progress.get().investigation?.dialogue ?? {};
  }

  /**
   * Write a new story state into the save, and check whether it resolves the mystery.
   *
   * The ending is recorded the moment its gate passes rather than being recomputed for display,
   * so "when did this player finish" is a fact in the save — which is what the Chapter 8 admin
   * panel will want, and what makes a resolution feel like an event rather than a rendering.
   */
  private commit(next: StoryState, mutate?: (data: InvestigationData) => InvestigationData): void {
    this.sync.patch((current) => {
      const base = current.investigation ?? emptyInvestigation();
      let data: InvestigationData = {
        ...base,
        clues: [...next.clues],
        events: [...next.events],
      };
      if (mutate) data = mutate(data);

      // The conclusion follows the player's current state; the *moment* they first reached one
      // does not. A player who keeps going and learns the rest should end up with the fuller
      // ending — endings are ordered most-specific-first precisely so that later knowledge wins
      // — while "when did they finish" stays the first time they got there.
      const ending = this.story.endingFor(next);
      if (ending && ending.id !== data.endingId) {
        data = {
          ...data,
          endingId: ending.id,
          endingAt: data.endingAt ?? new Date().toISOString(),
        };
      }

      return { ...current, investigation: data };
    });
    this.refresh();
  }

  private refresh(): void {
    this.view.set(this.computeView());
  }

  private computeView(): InvestigationView {
    const progress: ProgressData = this.sync.progress.get();
    const state = this.storyState();

    let dialogue: InvestigationView['dialogue'] = null;
    if (this.openDialogueId && this.openSignalId) {
      const position = (progress.investigation ?? emptyInvestigation()).dialogue[
        this.openDialogueId
      ];
      const node = position
        ? this.story.resolveNode(this.openDialogueId, position.nodeId, state)
        : undefined;
      if (node) {
        dialogue = {
          signalId: this.openSignalId,
          dialogueId: this.openDialogueId,
          label: this.openSignalLabel ?? this.openSignalId,
          node,
        };
      }
    }

    return {
      state,
      clues: this.story.heldClues(state),
      progress: this.story.progress(state),
      ending: this.story.endingFor(state),
      log: [...progress.frequencyLog].sort((a, b) => a.khz - b.khz),
      dialogue,
      interrupted: this.interrupted,
    };
  }

  /**
   * A compact projection of progression for the server to store in queryable columns.
   *
   * Explicitly a *projection*: the save's JSON stays authoritative and this is never read back
   * into the game. It exists so an operator can ask "how far has everyone got" without scanning
   * a jsonb blob per account.
   */
  summary(): { storyId: string; clueCount: number; clueTotal: number; endingId: string | null } {
    const state = this.storyState();
    const progress = this.story.progress(state);
    return {
      storyId: this.story.id,
      clueCount: progress.cluesHeld,
      clueTotal: progress.cluesTotal,
      endingId: (this.sync.progress.get().investigation ?? emptyInvestigation()).endingId,
    };
  }
}
